import { V1ExternalAccountInfo } from '@blk/portfolio-security-server/accountInfo';
import { PortfolioEditorMode } from '@ddt-web/config';
import { PasAsset } from '@ddt-web/pas';
import { Action } from '@ngrx/store';

import { AccountErrors, PartnerAccount, PartnerAcknowledgement, PartnerAuthenticationInfo, PartnerPlatform } from '../../models';
import { AnalysisState } from './analysis.state';
import { ApbModalState } from './apb-state';
import { GenerateReportDataState } from './generate-report-data.state';
import { ImportPartnerPortfoliosState } from './import-partner-portfolios.state';
import { IretireUserDataState } from './iretire-user-data.state';
import { PerformanceState } from './performance.state';

export const UPDATE_PE_MODEL_VISIBLE = '[PE State] - update isModalVisible';
export const UPDATE_PE_MODEL_UNIQUE_ID = '[PE State] - update uniqueId';
export const UPDATE_PE_MODEL_COMPARISON_IDS = '[PE State] - update comparisonIds';
export const CLEAR_PE_MODEL_COMPARISON_IDS = '[PE State] - clear comparisonIds';
export const UPDATE_PE_MODEL_ENGAGEMENT_ID = '[PE State] - update engagementId';
export const CLEAR_PE_MODEL_ENGAGEMENT_ID = '[PE State] - clear engagementId';
export const CLEAR_IRETIRE_USER_DATA = 'iRetire - [Application State] - clear user data';
export const UPDATE_PE_MODEL_ASSET_CLASSES = '[PE State] - update asset classes';
export const UPDATE_PE_MODEL_STRESS_TESTS = '[PE State] - update stress tests';
export const UPDATE_PE_MODEL_AC_BENCHMARK = '[PE State] - update ac benchmark';
export const UPDATE_PE_MODEL_ASSET_TYPES = '[PE State] - update asset types';
export const UPDATE_PE_MODEL_URL_IDS = '[PE State] - update URL Ids';
export const UPDATE_PE_MODEL_PE_MODE = '[PE State] - update PE Mode';
export const UPDATE_APB_MODAL_DISMISSAL = '[APB State] - update isModalDismissed';
export const UPDATE_HYPOTHETICAL_GROWTH_MULTIPLIER = '[Performance State] - update hypotheticalGrowthMultiplier';
export const UPDATE_HYPOTHETICAL_GROWTH_START = 'Portfolio Analyser - [Performance State] - update hypotheticalGrowthStartTimestamp';
export const UPDATE_KEY_RATE_SELECTED_CURRENCY = '[Risk State] - update keyRateSelectedCurrency';
export const UPDATE_CUSTOM_ADVISORY_FEE = '[Performance State] - update customAdvisoryFee';
export const UPDATE_SELECTED_BENCHMARK_FOR_CAPTURE_RATIO = '[Performance State] - update selectedBenchmarkForCaptureRatio';
export const UPDATE_FUNDS_TO_EXPLORE = '[FundsToExplore State] - update fundsToExplore';
export const UPDATE_FUNDS_TO_EXPLORE_IS_WIDGET_COVERED = '[FundsToExplore State] - update widget covered';
export const UPDATE_PORTFOLIO_IDS = '[Portfolio Ids State] - update portfolioIds';
export const UPDATE_SELECTED_PORTFOLIO_INDEX = '[PortfolioIndex State] - update selectedPortfolioIndex';
export const GET_HYPOTHETICAL_GROWTH_MULTIPLIER = '[Performance State] - get hypotheticalGrowthMultiplier';
export const GET_KEY_RATE_SELECTED_CURRENCY = '[Risk State] - get keyRateSelectedCurrency';
export const GET_CUSTOM_ADVISORY_FEE = '[Performance State] - get customAdvisoryFee';
export const GET_FUNDS_TO_EXPLORE = '[FundsToExplore State] - get fundsToExplore';
export const GET_SELECTED_PORTFOLIO_INDEX = '[PortfolioIndex State] - get selectedPortfolioIndex';
export const GET_RESUME_EXCEL_DOWNLOAD = '[ResumeExcelDownload State] - get shouldResumeExcelDownload';
export const UPDATE_RESUME_EXCEL_DOWNLOAD = '[ResumeExcelDownload State] - update shouldResumeExcelDownload';
export const UPDATE_GENERATE_REPORT_DATA = '[Generate Report Data State] - update addition info on generate report';
export const UPDATE_FEATURE_FLAGS = '[Feature Flags State] - update feature flags';
export const UPDATE_ANALYSIS_STATE = '[Analysis State] - update the analysis state';
export const UPDATE_CMA_IS_TRANSIENT_PORTFOLIO_EDITED = '[CMA State] - update isTransientPortfolioEdited';
export const UPDATE_IRETIRE_USER_DATA_STATE = 'iRetire - [Application State] - update user data';
export const GET_IRETIRE_USER_DATA_STATE = 'iRetire - [Application State] - get user data';
export const UPDATE_IQ_STATUS_OR_AREA_OF_FOCUS = 'Update IQ status or area of focus';
export const UPDATE_PORTFOLIOS_TAX_QUALIFIED_FLAG = 'Update portfolios tax qualified flag';
export const UPDATE_ME_SELECTED_MODELS_DATA = 'Models Experience - Update selected models for mini compare';
export const UPDATE_PORTFOLIOS_TAX_QUALIFIED_TYPE = 'Update portfolios tax qualified type';
export const UPDATE_IS_DISMISSED_FUNDS_UPDATED = 'Update isDismissedFundsUpdated';
export const UPDATE_IMPORT_PARTNER_PORTFOLIOS_STATE = 'Update importPartnerPortfoliosState';
export const UPDATE_IMPORT_PARTNER_PORTFOLIOS_PARTNER_PLATFORM = 'Update importPartnerPortfoliosState partnerPlatform';
export const UPDATE_IMPORT_PARTNER_PORTFOLIOS_SELECTED_PORTFOLIOS = 'Update importPartnerPortfoliosState selectedPortfolios';
export const UPDATE_IMPORT_PARTNER_PORTFOLIOS_IS_REFRESH_NEEDED = 'Update importPartnerPortfoliosState isRefreshNeeded';
export const UPDATE_IMPORT_PARTNER_PORTFOLIOS_PORTFOLIO_DATA = 'Update importPartnerPortfoliosState portfolioData';
export const UPDATE_IMPORT_PARTNER_PORTFOLIOS_RESOLVED_ASSETS = 'Update importPartnerPortfoliosState resolvedAssets';
export const UPDATE_IMPORT_PARTNER_PORTFOLIOS_ACCOUNT_ERROR_MAP = 'Update importPartnerPortfoliosState errorMap';
export const UPDATE_PROPOSAL_PORTFOLIO_NAME = '[Proposal State] - update proposal portfolio name';
export const UPDATE_PROPOSAL_METRICS = '[Proposal State] - update proposal metrics';
export const UPDATE_PROPOSAL_PURPOSE = '[Proposal State] - update proposal purpose';
export const UPDATE_PARTNER_AUTHENTICATION_INFO = '[Partner Authentication] - Update partner authentication info';
export const UPDATE_PARTNER_ACKNOWLEDGEMENT_STATE = '[Partner Acknowledgement] - Update partner acknowledgement state';

export class UpdatePEVisibilityAction implements Action {
	public readonly type = UPDATE_PE_MODEL_VISIBLE;
	constructor(public payload: boolean) { }
}

export class UpdatePEModelUniqueIdAction implements Action {
	public readonly type = UPDATE_PE_MODEL_UNIQUE_ID;
	constructor(public payload: string) { }
}

export class UpdatePEModelComparisonIdsAction implements Action {
	public readonly type = UPDATE_PE_MODEL_COMPARISON_IDS;
	constructor(public payload: Array<string>) { }
}

export class ClearPEModelComparisonIdsAction implements Action {
	public readonly type = CLEAR_PE_MODEL_COMPARISON_IDS;
}

export class UpdatePEModelEngagementIdAction implements Action {
	public readonly type = UPDATE_PE_MODEL_ENGAGEMENT_ID;
	constructor(public payload: string) { }
}

export class ClearPEModelEngagementIdAction implements Action {
	public readonly type = CLEAR_PE_MODEL_ENGAGEMENT_ID;
}

export class UpdateApbModalVisibilityAction implements Action {
	public readonly type = UPDATE_APB_MODAL_DISMISSAL;
	constructor(public payload: ApbModalState[]) { }
}

export class UpdateProposalPortfolioNameAction implements Action {
	public readonly type = UPDATE_PROPOSAL_PORTFOLIO_NAME;
	constructor(public payload: string) { }
}

export class UpdateProposalMetricsAction implements Action {
	public readonly type = UPDATE_PROPOSAL_METRICS;
	constructor(public payload: any) { }
}

export class UpdateProposalPurposeAction implements Action {
	public readonly type = UPDATE_PROPOSAL_PURPOSE;
	constructor(public payload: any) { }
}

export class UpdateHypotheticalGrowthMultiplierAction implements Action {
	public readonly type = UPDATE_HYPOTHETICAL_GROWTH_MULTIPLIER;
	constructor(public payload: any) { }
}

export class UpdateHypotheticalGrowthStartAction implements Action {
	public readonly type = UPDATE_HYPOTHETICAL_GROWTH_START;
	constructor(public payload: { hypotheticalGrowthStartTimestamp: number }) { }
}

export class UpdateKeyRateSelectedCurrencyAction implements Action {
	public readonly type = UPDATE_KEY_RATE_SELECTED_CURRENCY;
	constructor(public payload: any) { }
}

export class UpdateCustomAdvisoryFee implements Action {
	public readonly type = UPDATE_CUSTOM_ADVISORY_FEE;
	constructor(public payload: PerformanceState) { }
}

export class UpdateSelectedBenchmarkForCaptureRatio implements Action {
	public readonly type = UPDATE_SELECTED_BENCHMARK_FOR_CAPTURE_RATIO;
	constructor(public payload: PerformanceState) { }
}

export class UpdateFundsToExploreAction implements Action {
	public readonly type = UPDATE_FUNDS_TO_EXPLORE;
	constructor(public payload: { [portfolioId: string]: any[] }) { }
}

export class UpdatePortfolioIdsAction implements Action {
	public readonly type = UPDATE_PORTFOLIO_IDS;
	constructor(public payload: Array<string>) { }
}

export class UpdateSelectedPortfolioIndexAction implements Action {
	public readonly type = UPDATE_SELECTED_PORTFOLIO_INDEX;
	constructor(public payload: number) { }
}

export class UpdateResumeExcelDownloadAction implements Action {
	public readonly type = UPDATE_RESUME_EXCEL_DOWNLOAD;
	constructor(public payload: boolean) { }
}

export class UpdateGenerateReportDataAction implements Action {
	public readonly type = UPDATE_GENERATE_REPORT_DATA;
	constructor(public payload: GenerateReportDataState) { }
}

export class UpdateFeatureFlagsAction implements Action {
	public readonly type = UPDATE_FEATURE_FLAGS;
	constructor(public payload: string[]) { }
}

export class UpdateAnalysisStateAction implements Action {
	public readonly type = UPDATE_ANALYSIS_STATE;
	constructor(public payload: AnalysisState) { }
}

export class UpdateCmaIsTransientPortfolioEdited implements Action {
	public readonly type = UPDATE_CMA_IS_TRANSIENT_PORTFOLIO_EDITED;
	constructor(public payload: boolean) { }
}

export class UpdatePEModelAssetClassesAction implements Action {
	public readonly type = UPDATE_PE_MODEL_ASSET_CLASSES;
	constructor(public payload: Array<string>) { }
}

export class UpdatePEModelStressTestsAction implements Action {
	public readonly type = UPDATE_PE_MODEL_STRESS_TESTS;
	constructor(public payload: Array<string>) { }
}

export class UpdatePEModelAcBenchmarkAction implements Action {
	public readonly type = UPDATE_PE_MODEL_AC_BENCHMARK;
	constructor(public payload: string) { }
}

export class UpdatePEModelAssetTypesAction implements Action {
	public readonly type = UPDATE_PE_MODEL_ASSET_TYPES;
	constructor(public payload: Array<{ displayName: string; label?: string; count?: number }>) { }
}

export class UpdatePEModelUrlIdsAction implements Action {
	public readonly type = UPDATE_PE_MODEL_URL_IDS;
	constructor(public payload: Array<string>) { }
}

export class UpdatePEModelPEModeAction implements Action {
	public readonly type = UPDATE_PE_MODEL_PE_MODE;
	constructor(public payload: PortfolioEditorMode) { }
}

export class UpdateIretireUserDataState implements Action {
	public readonly type = UPDATE_IRETIRE_USER_DATA_STATE;
	constructor(public payload: IretireUserDataState) { }
}

export class ClearIretireUserDataAction implements Action {
	public readonly type = CLEAR_IRETIRE_USER_DATA;
}

export class UpdateIqStatusOrAreaOfFocus implements Action {
	public readonly type = UPDATE_IQ_STATUS_OR_AREA_OF_FOCUS;
	constructor(public payload: string) { }
}

export class UpdatePortfoliosTaxQualifiedType implements Action {
	public readonly type = UPDATE_PORTFOLIOS_TAX_QUALIFIED_TYPE;
	constructor(public payload: string) { }
}

export class UpdateMeSelectedModelsData implements Action {
	public readonly type = UPDATE_ME_SELECTED_MODELS_DATA;
	constructor(public payload: { [key: string]: any }) { }
}

export class UpdateIsDismissedFundsUpdated implements Action {
	public readonly type = UPDATE_IS_DISMISSED_FUNDS_UPDATED;
	constructor(public payload: boolean) { }
}

export class UpdateImportPartnerPortfoliosState implements Action {
	public readonly type = UPDATE_IMPORT_PARTNER_PORTFOLIOS_STATE;
	constructor(public payload: ImportPartnerPortfoliosState) { }
}

export class UpdateImportPartnerPortfoliosPartnerPlatform implements Action {
	public readonly type = UPDATE_IMPORT_PARTNER_PORTFOLIOS_PARTNER_PLATFORM;
	constructor(public payload: PartnerPlatform) { }
}

export class UpdateImportPartnerPortfoliosPortfolioData implements Action {
	public readonly type = UPDATE_IMPORT_PARTNER_PORTFOLIOS_PORTFOLIO_DATA;
	constructor(public payload: PartnerAccount[]) { }
}
export class UpdateImportPartnerPortfoliosSelectedPortfolios implements Action {
	public readonly type = UPDATE_IMPORT_PARTNER_PORTFOLIOS_SELECTED_PORTFOLIOS;
	constructor(public payload: Map<string, V1ExternalAccountInfo>) { }
}
export class UpdateImportPartnerPortfoliosIsRefreshNeeded implements Action {
	public readonly type = UPDATE_IMPORT_PARTNER_PORTFOLIOS_IS_REFRESH_NEEDED;
	constructor(public payload: boolean) { }
}
export class UpdateImportPartnerPortfoliosResolvedAssets implements Action {
	public readonly type = UPDATE_IMPORT_PARTNER_PORTFOLIOS_RESOLVED_ASSETS;
	constructor(public payload: Map<string, Partial<PasAsset>>) { }
}

export class UpdateImportPartnerPortfoliosAccountErrorMap implements Action {
	public readonly type = UPDATE_IMPORT_PARTNER_PORTFOLIOS_ACCOUNT_ERROR_MAP;
	constructor(public payload: Map<string, AccountErrors>) { }
}

export class UpdatePartnerAuthenticationInfo implements Action {
	public readonly type = UPDATE_PARTNER_AUTHENTICATION_INFO;
	constructor(public payload: { partnerId: string; partnerAuthenticationInfo: PartnerAuthenticationInfo }) { }
}

export class UpdatePartnerAcknowledgementState implements Action {
	public readonly type = UPDATE_PARTNER_ACKNOWLEDGEMENT_STATE;
	constructor(public payload: PartnerAcknowledgement) { }
}

export type Actions =
	| UpdatePEVisibilityAction
	| UpdatePEModelUniqueIdAction
	| UpdatePEModelComparisonIdsAction
	| ClearPEModelComparisonIdsAction
	| UpdatePEModelEngagementIdAction
	| ClearPEModelEngagementIdAction
	| UpdatePEModelAssetClassesAction
	| UpdatePEModelStressTestsAction
	| UpdatePEModelAcBenchmarkAction
	| UpdatePEModelAssetTypesAction
	| UpdatePEModelUrlIdsAction
	| UpdatePEModelPEModeAction
	| UpdateApbModalVisibilityAction
	| UpdateProposalMetricsAction
	| UpdateProposalPortfolioNameAction
	| UpdateProposalPurposeAction
	| UpdateHypotheticalGrowthMultiplierAction
	| UpdateHypotheticalGrowthStartAction
	| UpdateCustomAdvisoryFee
	| UpdateSelectedBenchmarkForCaptureRatio
	| UpdateKeyRateSelectedCurrencyAction
	| UpdateFundsToExploreAction
	| UpdatePortfolioIdsAction
	| UpdateSelectedPortfolioIndexAction
	| UpdateResumeExcelDownloadAction
	| UpdateGenerateReportDataAction
	| UpdateFeatureFlagsAction
	| UpdateAnalysisStateAction
	| UpdateCmaIsTransientPortfolioEdited
	| UpdateIretireUserDataState
	| ClearIretireUserDataAction
	| UpdateIqStatusOrAreaOfFocus
	| UpdateMeSelectedModelsData
	| UpdatePortfoliosTaxQualifiedType
	| UpdateIsDismissedFundsUpdated
	| UpdateImportPartnerPortfoliosState
	| UpdateImportPartnerPortfoliosPartnerPlatform
	| UpdateImportPartnerPortfoliosPortfolioData
	| UpdateImportPartnerPortfoliosSelectedPortfolios
	| UpdateImportPartnerPortfoliosIsRefreshNeeded
	| UpdateImportPartnerPortfoliosResolvedAssets
	| UpdateImportPartnerPortfoliosAccountErrorMap
	| UpdatePartnerAuthenticationInfo
	| UpdatePartnerAcknowledgementState;
